import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useSelector } from 'react-redux';
import { capitalize, chain, defaultTo, isEmpty, isNil, map } from 'lodash';
import Swal from 'sweetalert2';
import ChatSection from './ChatSection';
import MonitoredAccounts from './MonitoredAccounts';
import ImpersonationSection from './ImpersonationSection.jsx';
import moment from 'moment';
import { formatCurrency } from '../../../../global/Helpers';
import store from '../../../../store/store';
import { BOOKKEEPER_ASSIGNMENT_TYPES } from '../../../../constants';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Link,
  Stack,
  Typography
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import Switch from 'react-switch';
import { authSelector } from '../../../../selectors/mainSelectors.js';
import {
  useAssignUserToBookkeeperMutation,
  useGetDeductionTotalsQuery,
  useGetUserBookkeeperAssignmentsQuery,
  useGetUserInfoQuery,
  useLazyExportUserDataQuery,
  useSetHoldMutation,
  useUnassignUserFromBookkeeperMutation,
  useUpdateUserMutation
} from '../../../../api/userApi.js';
import { selectedUserIdSelector } from '../../../../selectors/userSelectors.js';
import { isInternalSelector, isTaxExpertSelector } from '../../../../selectors/authSelectors.js';
import { COLORS } from '../../../../theme.js';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';

const MessageSection = () => {
  const auth = useSelector(authSelector);
  const selectedUserId = useSelector(selectedUserIdSelector);
  const isTaxExpert = useSelector(isTaxExpertSelector);
  const isInternal = useSelector(isInternalSelector);

  const [assignUserToBookkeeper] = useAssignUserToBookkeeperMutation();
  const [unassignUserFromBookkeeper] = useUnassignUserFromBookkeeperMutation();
  const [setHold] = useSetHoldMutation();
  const [updateUser] = useUpdateUserMutation();
  const [triggerExportUserData, { isFetching: isExportingUserData }] = useLazyExportUserDataQuery();

  const { data: userBookkeeperAssignments = [], isLoading: userBookkeeperAssignmentsLoading } =
    useGetUserBookkeeperAssignmentsQuery({ userId: selectedUserId });
  const { data: deductionTotals, isLoading: areDeductionTotalsLoading } = useGetDeductionTotalsQuery(selectedUserId);
  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery(selectedUserId);

  if (userBookkeeperAssignmentsLoading || areDeductionTotalsLoading || isUserInfoLoading) {
    return null;
  }

  const { id: userId, subscriptionInfo } = userInfo;
  const isPremium = subscriptionInfo.type === 'premium';
  const userType = [userInfo.referrer, subscriptionInfo.type, subscriptionInfo.status].filter(Boolean).join(' - ');

  const assignmentWithHold = userBookkeeperAssignments.find(
    (assignment) => assignment.assignmentType === BOOKKEEPER_ASSIGNMENT_TYPES.HOLD
  );
  const isOnHold = !isNil(assignmentWithHold);
  const currentBookkeeperHasHold = assignmentWithHold?.bookkeeperId === auth?.user?.id;
  const userHasAnyExtendedAssignments = userBookkeeperAssignments.some(
    (assignment) => assignment.assignmentType === BOOKKEEPER_ASSIGNMENT_TYPES.EXTENDED
  );

  /**
   * @description Toggle the autoPredict flag for this user
   */
  const handleToggleAutoPredict = async () => {
    try {
      const body = {
        autoPredict: !userInfo.autoPredict
      };
      updateUser({ userId, body });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'An unexpected error occurred. Please try again later. If the error persists, please make a ticket with the engineering team.'
      });
    }
  };

  /**
   * @description Put this user on/off hold
   */
  const handleToggleHold = async () => {
    try {
      const body = {
        userId,
        holdingBookkeeperId: store.getState().auth.user.id,
        isOnHold: !isOnHold
      };
      setHold(body);
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'An unexpected error occurred. Please try again later. If the error persists, please make a ticket with the engineering team.'
      });
    }
  };

  /**
   * @description Toggle the isAskEnabled flag for this user
   */
  const handleToggleAskAI = async () => {
    try {
      const body = {
        isAskEnabled: !userInfo.isAskEnabled
      };
      updateUser({ userId, body });
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'An unexpected error occurred. Please try again later. If the error persists, please make a ticket with the engineering team.'
      });
    }
  };

  const handleExportUserData = async () => {
    try {
      const data = await triggerExportUserData(userId).unwrap();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `user-${userId}-export-${moment().format('YYYY-MM-DD')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Export failed',
        text: 'Could not export user data. Please try again later.'
      });
    }
  };

  const handleToggleAssignment = () => {
    if (currentBookkeeperHasExtendedAssignment) {
      unassignUserFromBookkeeper({ userId, bookkeeperId: auth.user.id });
    } else {
      assignUserToBookkeeper({ userId });
    }
  };

  const churned = userInfo.welcomed === 1 && userInfo.active === 0 ? ' - inactive' : '';
  const trialStart = userInfo.subscribedAt ? moment(userInfo.subscribedAt).format('YYYY-MM-DD') : 'data unavailable';
  const jobs = userInfo.jobs && userInfo.jobs.join(', ');
  // Sorts deduction totals by year
  const sortedDeductionTotals = chain(deductionTotals).thru(Object.entries).sortBy('0').value();
  const extendedAssignments = userBookkeeperAssignments.filter(
    (assignment) => assignment.assignmentType === BOOKKEEPER_ASSIGNMENT_TYPES.EXTENDED
  );
  const currentBookkeeperHasExtendedAssignment = extendedAssignments.some(
    (assignment) => assignment.bookkeeperId === auth?.user?.id
  );

  return (
    <Stack
      data-testid='message-section'
      direction='column'
      sx={{
        width: '45%',
        height: '100%',
        overflow: 'auto'
      }}
    >
      <Box
        sx={{
          background: 'white',
          borderColor: '#d7dfe3',
          borderRadius: '4px',
          flexShrink: 0
        }}
      >
        <Stack direction='row'>
          <Box
            sx={{
              display: 'inline-block',
              p: 2
            }}
          >
            <Typography
              variant='h6'
              sx={{
                fontSize: '30px',
                my: '10px',
                color: '#455a64'
              }}
            >
              {userInfo.firstname} {userInfo.lastname}
              {isPremium && <FontAwesomeIcon icon={['fa', 'star']} color='gold' />}
              {churned}
            </Typography>
            <Box
              sx={{
                py: 2
              }}
            >
              <Typography variant='body2'>Jobs: {jobs}</Typography>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 4,
                  mt: 1
                }}
              >
                {userInfo.car === 1 && <FontAwesomeIcon icon={['fa', 'car']} />}
                {userInfo.home === 1 && <FontAwesomeIcon icon={['fa', 'home']} />}
                {userInfo.meals === 1 && <FontAwesomeIcon icon={['fa', 'utensils']} />}
                {userInfo.travel === 1 && <FontAwesomeIcon icon={['fa', 'plane']} />}
              </Stack>
            </Box>
          </Box>
          <Box
            sx={{
              p: 2,
              textAlign: 'right',
              width: '180px',
              ml: 'auto',
              verticalAlign: 'top',
              display: 'inline-block'
            }}
          >
            <ImpersonationSection />
            {isTaxExpert && (
              <Button
                variant='contained'
                fullWidth
                sx={{ backgroundColor: '#999999', borderRadius: '30px', height: '50px', marginTop: '12px' }}
                onClick={() => handleToggleAssignment()}
              >
                {currentBookkeeperHasExtendedAssignment ? 'Unassign self' : 'Assign to self'}
              </Button>
            )}
            <Stack
              direction='column'
              sx={{
                mt: 1,
                textAlign: 'left'
              }}
            >
              {!isEmpty(extendedAssignments) && (
                <>
                  <Typography component='span'>Assignments: </Typography>
                  {extendedAssignments.map((assignment) => {
                    const bookkeeper = assignment.bookkeeper;
                    const bookkeeperFullName = `${bookkeeper.firstname} ${bookkeeper.lastname}`;

                    const multipleFullName =
                      map(extendedAssignments, ({ bookkeeper: b }) => `${b.firstname} ${b.lastname}`).filter(
                        (name) => name === bookkeeperFullName
                      ).length > 1;

                    const label = multipleFullName ? `${bookkeeperFullName} (${bookkeeper.email})` : bookkeeperFullName;
                    return (
                      <Chip
                        color='primary'
                        variant='outlined'
                        sx={{
                          my: 0.5,
                          width: 'fit-content',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            overflow: 'visible'
                          }
                        }}
                        key={assignment.id}
                        label={label}
                        onDelete={() => unassignUserFromBookkeeper({ userId, bookkeeperId: bookkeeper.id })}
                      />
                    );
                  })}
                </>
              )}
            </Stack>
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                gap: 1,
                mt: 1
              }}
            >
              <Typography>Ask AI</Typography>
              <Switch onColor={COLORS.AMETHYST} onChange={handleToggleAskAI} checked={userInfo.isAskEnabled} />
            </Stack>
          </Box>
        </Stack>
        <Accordion>
          <AccordionSummary expandIcon={<FontAwesomeIcon icon={faChevronDown} />}>
            <Typography>Additional Info</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack
              direction='column'
              sx={{
                gap: 1
              }}
            >
              {isInternal && (
                <Button
                  variant='contained'
                  color='primary'
                  size='small'
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportUserData}
                  disabled={isExportingUserData}
                  sx={{ mb: 1, alignSelf: 'flex-start' }}
                >
                  {isExportingUserData ? 'Exporting...' : 'Export user data'}
                </Button>
              )}
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Typography variant='body1'>Auto Predict</Typography>
                <Switch
                  handleDiameter={22}
                  height={24}
                  width={48}
                  onColor={COLORS.AMETHYST}
                  onChange={handleToggleAutoPredict}
                  checked={userInfo.autoPredict}
                />
              </Stack>
              <Stack
                direction='row'
                sx={{
                  alignItems: 'center',
                  gap: 2
                }}
              >
                <Typography variant='body1'>Hold</Typography>
                <Switch
                  disabled={
                    (isOnHold && (!currentBookkeeperHasHold || userHasAnyExtendedAssignments)) ||
                    (!isOnHold && (!isNil(assignmentWithHold) || userHasAnyExtendedAssignments))
                  }
                  handleDiameter={22}
                  height={24}
                  width={48}
                  onColor={COLORS.AMETHYST}
                  onChange={handleToggleHold}
                  checked={isOnHold}
                />
              </Stack>
              {userInfo.qdeckUrl ? (
                <Typography variant='body1'>
                  <Link href={userInfo.qdeckUrl} target='_blank' rel='noopener noreferrer'>
                    User ID
                  </Link>
                  : {userInfo.id}
                </Typography>
              ) : (
                <Typography variant='body1'>User ID: {userInfo.id}</Typography>
              )}
              <Typography variant='body1'>
                Email:{' '}
                <Link href={`mailto:${userInfo.email}`} target='_blank' rel='noopener noreferrer'>
                  {userInfo.email}
                </Link>
              </Typography>
              <Typography variant='body1'>Phone: {userInfo.phone}</Typography>
              <Typography variant='body1'>Messaging Platform: {userInfo.platform}</Typography>
              <Typography variant='body1'>User Type: {userType}</Typography>
              <Typography variant='body1'>Tax Pro: {defaultTo(userInfo.assignedTaxPro, 'UNASSIGNED')}</Typography>
              {sortedDeductionTotals.map(([year, amount]) => (
                <Typography variant='body1' key={year}>
                  {year} Deductions: {formatCurrency(amount)}
                </Typography>
              ))}
              <Typography variant='body1'>Trial start: {trialStart}</Typography>
              <Typography variant='body1'>Tax rate: {userInfo.tax_rate}%</Typography>
              <Typography variant='body1'>Income Type: {defaultTo(userInfo.income_type, '[]')}</Typography>
              <Typography variant='body1'>Filing status: {capitalize(userInfo.filing_status)}</Typography>
              <Typography variant='body1'>Dependents: {userInfo.dependents ? 'True' : 'False'}</Typography>
              <Typography variant='body1'>Tax situations: {'Support in progress...'}</Typography>
              <Typography variant='body1'>Goals: {'Support in progress...'}</Typography>
            </Stack>
            <MonitoredAccounts />
          </AccordionDetails>
        </Accordion>
      </Box>
      <ChatSection />
    </Stack>
  );
};

export default MessageSection;
